import {
  Color,
  Mapping,
  Mesh,
  NearestFilter,
  OrthographicCamera,
  PixelFormat,
  PlaneGeometry,
  RGBAFormat,
  Scene,
  ShaderMaterial,
  SRGBColorSpace,
  Texture,
  WebGLRenderer,
  WebGLRenderTarget,
} from '@seddi/three';

const premultiplyFragmentShader = `
  uniform sampler2D uTexture;
  varying vec2 vUv;

  void main() {
    vec4 color = texture2D(uTexture, vUv);
    color.rgb *= color.a;
    gl_FragColor = color;
  }
`;

const premultiplyVertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export function premultiplyAlphaSRGB(renderer: WebGLRenderer, texture: Texture): Texture {
  const { height, width } = texture.image;

  // Use a WebGLRenderTarget to render the output to a new texture
  const renderTarget = new WebGLRenderTarget(width, height, {
    colorSpace: SRGBColorSpace,
    format: RGBAFormat,
    magFilter: NearestFilter,
    minFilter: NearestFilter,
  });

  // Create an orthographic camera to render a 2D quad
  const camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const scene = new Scene();

  // Create a plane geometry that fills the entire camera view
  const geometry = new PlaneGeometry(2, 2);

  // Use a ShaderMaterial to apply our custom premultiplying shader
  const material = new ShaderMaterial({
    fragmentShader: premultiplyFragmentShader,
    uniforms: {
      uTexture: { value: texture },
    },
    vertexShader: premultiplyVertexShader,
  });

  const mesh = new Mesh(geometry, material);
  scene.add(mesh);

  // Set the original texture's color space to sRGB.
  // WebGL will then correctly handle the sRGB -> linear conversion when the shader samples the texture.
  // texture.colorSpace = SRGBColorSpace;
  // texture.needsUpdate = true;

  // Preserve the original state of the renderer
  const originalAutoClear = renderer.autoClear;
  const originalClearAlpha = renderer.getClearAlpha();
  const originalClearColor = renderer.getClearColor(new Color());
  const originalRenderTarget = renderer.getRenderTarget();

  // Render the scene to the new texture
  renderer.setRenderTarget(renderTarget);
  renderer.setClearColor(0x000000, 0); // Clear with transparent black
  renderer.autoClear = true;
  renderer.render(scene, camera);

  // Copy the pixel data from the render target's texture to the new texture
  // NOTE: This does not work in our current base version of Three.js
  // renderer.copyTextureToTexture(new Vector2(0, 0), renderTarget.texture, premultipliedTexture);

  // Create a buffer to hold the pixel data
  const pixels = new Uint8Array(4 * width * height);
  renderer.readRenderTargetPixels(renderTarget, 0, 0, width, height, pixels);
  const pixelsData = new ImageData(new Uint8ClampedArray(pixels), width, height);
  // Create a new Texture from the ImageData
  const premultipliedTexture = new Texture(
    pixelsData,
    texture.mapping as Mapping,
    texture.wrapS,
    texture.wrapT,
    texture.magFilter,
    texture.minFilter,
    texture.format as PixelFormat,
    texture.type,
    texture.anisotropy,
    texture.colorSpace,
  );

  // Set the correct properties on the new texture
  premultipliedTexture.flipY = false;
  premultipliedTexture.needsUpdate = true;

  // Restore the original renderer state
  renderer.setRenderTarget(originalRenderTarget);
  renderer.setClearColor(originalClearColor, originalClearAlpha);
  renderer.autoClear = originalAutoClear;

  // Dispose of the temporary resources to prevent memory leaks
  geometry.dispose();
  material.dispose();
  renderTarget.dispose();

  return premultipliedTexture;
}
